// ── Sale expenses — the "Additional Charges & Expenses" rows on a sale ──────
// Shared transactional logic (not presentational), so it lives here once
// instead of being duplicated — same reasoning as utils/dueGroups.ts. Used by
// Sales.tsx's create / edit / delete paths.
//
// Each row becomes one `saleExpenses` doc linked to the sale, plus (when paid
// now) a `bankTransactions` doc + bank balance move, plus one deterministic
// `sale_expense__{id}` journal entry. A row marked "charge to customer" is
// also added to the sale's own totalAmountUSD by the caller (see
// saleExpenseTotals) — that's what puts it on the invoice and in AR.
//
// Usage inside one runTransaction (reads before writes):
//   const plan = planSaleExpenses(db, rows, originals);      // before the tx
//   const accts = await readSaleExpenseAccounts(tx, plan);   // read phase
//   applySaleExpenses(tx, db, plan, accts, ctx);             // write phase
// The caller owns the counters/{businessId} write (a transaction can't write
// that doc twice) — bump `saleExpense` by plan.creates.length.

import {
  collection, doc, serverTimestamp,
  type DocumentReference, type Firestore, type Transaction,
} from 'firebase/firestore';
import type { BankAccount, SaleExpense, SaleExtraCost } from '../types';
import { buildEntry, postEntry, entryId, linesSaleExpense } from './ledger';

export const SALE_EXPENSE_CATEGORIES = [
  'Shipping / Courier', 'Insurance', 'Export Duty / Customs', 'Packaging',
  'Certification', 'Bank / Transfer Fees', 'Other',
];

export interface SaleExpenseRow {
  _key:          string;
  // Set only when loaded from an existing saleExpenses doc (editing a sale),
  // so save-time can tell "still here, maybe changed" from new / removed.
  _existingId?:  string;
  category:      string;
  description:   string;
  amount:        string;
  date:          string;
  vendorEntityId: string;
  chargeToCustomer: boolean;
  payNow:        boolean;
  bankAccountId: string;
  dueDate:       string;
}

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
function stripUndef<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}

let _rowKey = 0;
export function newSaleExpenseRow(date: string): SaleExpenseRow {
  return {
    _key: `se${++_rowKey}`, category: SALE_EXPENSE_CATEGORIES[0], description: '', amount: '', date,
    vendorEntityId: '', chargeToCustomer: false, payNow: false, bankAccountId: '', dueDate: '',
  };
}
export function saleExpenseToRow(se: SaleExpense): SaleExpenseRow {
  return {
    _key: se.id, _existingId: se.id,
    category: se.category, description: se.description, amount: String(se.amountUSD), date: se.date,
    vendorEntityId: se.vendorEntityId ?? '', chargeToCustomer: !!se.chargeToCustomer,
    payNow: se.paymentStatus === 'paid', bankAccountId: se.bankAccountId ?? '', dueDate: se.dueDate ?? '',
  };
}

export const rowAmount = (row: SaleExpenseRow) => round2(parseFloat(row.amount) || 0);

/** Charged = added to the invoice; own = our selling cost. */
export function saleExpenseTotals(rows: SaleExpenseRow[]): { charged: number; own: number } {
  let charged = 0, own = 0;
  for (const r of rows) {
    const a = rowAmount(r);
    if (a <= 0) continue;
    if (r.chargeToCustomer) charged += a; else own += a;
  }
  return { charged: round2(charged), own: round2(own) };
}

export function validateSaleExpenseRows(rows: SaleExpenseRow[]): string {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i], n = i + 1;
    if (!row.description.trim())          return `Charge / Expense ${n}: Enter a description.`;
    if (!(rowAmount(row) > 0))            return `Charge / Expense ${n}: Enter a valid amount.`;
    if (row.payNow && !row.bankAccountId) return `Charge / Expense ${n}: Choose the account it was paid from.`;
  }
  return '';
}

export interface SaleExpensePlan {
  creates: { row: SaleExpenseRow; ref: DocumentReference; txRef: DocumentReference | null }[];
  updates: { row: SaleExpenseRow; original: SaleExpense; newTxRef: DocumentReference | null }[];
  deletes: SaleExpense[];
  accRefs: Map<string, DocumentReference>;
}

/** Diffs the form's rows against what was loaded (by _existingId). Pass rows = [] to delete them all. */
export function planSaleExpenses(db: Firestore, rows: SaleExpenseRow[], originals: SaleExpense[]): SaleExpensePlan {
  const byExistingId = new Map(rows.filter(r => r._existingId).map(r => [r._existingId!, r]));
  const updates: SaleExpensePlan['updates'] = [];
  const deletes: SaleExpense[] = [];
  originals.forEach(orig => {
    const row = byExistingId.get(orig.id);
    if (!row) { deletes.push(orig); return; }
    // Only an unpaid→paid move needs a brand-new bank transaction; an
    // already-paid row reuses (updates in place) its existing one.
    const needsNewTx = row.payNow && (orig.paymentStatus !== 'paid' || !orig.bankTransactionId);
    updates.push({ row, original: orig, newTxRef: needsNewTx ? doc(collection(db, 'bankTransactions')) : null });
  });
  const creates = rows.filter(r => !r._existingId).map(row => ({
    row,
    ref: doc(collection(db, 'saleExpenses')),
    txRef: row.payNow ? doc(collection(db, 'bankTransactions')) : null,
  }));

  const accIds = new Set<string>();
  creates.forEach(c => { if (c.row.payNow && c.row.bankAccountId) accIds.add(c.row.bankAccountId); });
  updates.forEach(({ row, original }) => {
    if (row.payNow && row.bankAccountId) accIds.add(row.bankAccountId);
    if (original.paymentStatus === 'paid' && original.bankAccountId) accIds.add(original.bankAccountId);
  });
  deletes.forEach(o => { if (o.paymentStatus === 'paid' && o.bankAccountId) accIds.add(o.bankAccountId); });
  return { creates, updates, deletes, accRefs: new Map([...accIds].map(id => [id, doc(db, 'bankAccounts', id)])) };
}

/** The summary written onto SaleInvoice.extraCosts — ids are known before the tx runs. */
export function extraCostsOf(plan: SaleExpensePlan): SaleExtraCost[] {
  const rows = [
    ...plan.updates.map(u => ({ id: u.original.id, row: u.row })),
    ...plan.creates.map(c => ({ id: c.ref.id, row: c.row })),
  ];
  return rows
    .filter(({ row }) => rowAmount(row) > 0)
    .map(({ id, row }) => ({
      saleExpenseId: id, category: row.category, description: row.description.trim(),
      amountUSD: rowAmount(row), chargeToCustomer: row.chargeToCustomer,
    }));
}

/** Read phase — every bank account any create/update/delete might touch. */
export async function readSaleExpenseAccounts(tx: Transaction, plan: SaleExpensePlan): Promise<Map<string, BankAccount>> {
  const out = new Map<string, BankAccount>();
  for (const [id, ref] of plan.accRefs) {
    const s = await tx.get(ref);
    if (s.exists()) out.set(id, s.data() as BankAccount);
  }
  // A row being paid NOW must point at a live account; a since-deleted
  // account on an old paid row is tolerated (nothing left to move back).
  for (const c of plan.creates) {
    if (c.row.payNow && !out.has(c.row.bankAccountId)) throw new Error('A selected bank account no longer exists.');
  }
  for (const u of plan.updates) {
    if (u.row.payNow && !out.has(u.row.bankAccountId)) throw new Error('A selected bank account no longer exists.');
  }
  return out;
}

export interface SaleExpenseContext {
  businessId:       string;
  userId:           string;
  baseCurrency:     string;
  prefix:           string;   // settings.saleExpensePrefix || 'SE'
  counterStart:     number;   // counters/{businessId}.saleExpense as read in this tx
  saleId:           string;
  saleInvoiceNo:    string;
  saleDate:         string;
  customerEntityId: string;
}

/** Write phase. */
export function applySaleExpenses(
  tx: Transaction, db: Firestore, plan: SaleExpensePlan, accts: Map<string, BankAccount>, ctx: SaleExpenseContext,
): void {
  const balanceDelta = new Map<string, number>();   // accountId -> amount to ADD to currentBalance
  const move = (accId: string | undefined, amt: number) => {
    if (!accId || !accts.has(accId)) return;
    balanceDelta.set(accId, round2((balanceDelta.get(accId) ?? 0) + amt));
  };
  const post = (id: string, expenseNo: string, row: SaleExpenseRow, amt: number, date: string, currency: string) => {
    postEntry(tx, db, buildEntry({
      businessId: ctx.businessId, sourceType: 'sale_expense', sourceId: id,
      sourceCollection: 'saleExpenses', date, currency, createdBy: ctx.userId,
      note: `${expenseNo} — ${row.description.trim()}`,
      lines: linesSaleExpense(amt, row.chargeToCustomer, row.payNow ? { bankAccountId: row.bankAccountId } : 'accrued',
        ctx.customerEntityId, row.vendorEntityId || undefined),
    }), entryId('sale_expense', id));
  };
  const bankTx = (row: SaleExpenseRow, amt: number, date: string, referenceId: string) => stripUndef({
    businessId: ctx.businessId, accountId: row.bankAccountId, accountName: accts.get(row.bankAccountId)?.name,
    currency: accts.get(row.bankAccountId)?.currency ?? ctx.baseCurrency, direction: 'out', amount: amt, date,
    description: row.description.trim(), referenceType: 'sale_expense', referenceId, category: row.category,
  });
  const docFields = (row: SaleExpenseRow, amt: number, date: string, currency: string, bankTransactionId?: string) => ({
    businessId: ctx.businessId, category: row.category, description: row.description.trim(),
    amountUSD: amt, currency, date, chargeToCustomer: row.chargeToCustomer,
    saleId: ctx.saleId, saleInvoiceNo: ctx.saleInvoiceNo, customerEntityId: ctx.customerEntityId,
    vendorEntityId: row.vendorEntityId || undefined,
    paymentStatus: row.payNow ? 'paid' : 'unpaid', paidUSD: row.payNow ? amt : 0,
    bankAccountId: row.payNow ? row.bankAccountId : undefined,
    bankAccountName: row.payNow ? accts.get(row.bankAccountId)?.name : undefined,
    paidDate: row.payNow ? date : undefined,
    bankTransactionId: row.payNow ? bankTransactionId : undefined,
    dueDate: row.payNow ? undefined : (row.dueDate || undefined),
  });

  plan.deletes.forEach(orig => {
    tx.delete(doc(db, 'saleExpenses', orig.id));
    tx.delete(doc(db, 'journalEntries', entryId('sale_expense', orig.id)));
    if (orig.paymentStatus === 'paid') {
      if (orig.bankTransactionId) tx.delete(doc(db, 'bankTransactions', orig.bankTransactionId));
      move(orig.bankAccountId, orig.amountUSD ?? 0);
    }
  });

  plan.updates.forEach(({ row, original, newTxRef }) => {
    const amt  = rowAmount(row);
    const date = row.date || ctx.saleDate;
    const currency = (row.payNow ? accts.get(row.bankAccountId)?.currency : undefined) ?? original.currency ?? ctx.baseCurrency;
    // Full reversal of the old paid state, then re-apply the new one — the
    // net-delta map handles same-account cancellation.
    if (original.paymentStatus === 'paid') move(original.bankAccountId, original.amountUSD ?? 0);
    let bankTransactionId = original.bankTransactionId;
    if (row.payNow) {
      if (newTxRef) {
        tx.set(newTxRef, { ...bankTx(row, amt, date, original.id), createdAt: new Date().toISOString(), createdBy: ctx.userId });
        bankTransactionId = newTxRef.id;
      } else if (original.bankTransactionId) {
        const t = bankTx(row, amt, date, original.id);
        tx.update(doc(db, 'bankTransactions', original.bankTransactionId), stripUndef({
          accountId: t.accountId, accountName: t.accountName, currency: t.currency,
          amount: amt, date, description: t.description, category: t.category,
        }));
      }
      move(row.bankAccountId, -amt);
    } else if (original.paymentStatus === 'paid' && original.bankTransactionId) {
      tx.delete(doc(db, 'bankTransactions', original.bankTransactionId));
      bankTransactionId = undefined;
    }
    tx.set(doc(db, 'saleExpenses', original.id), stripUndef({
      ...docFields(row, amt, date, currency, bankTransactionId),
      expenseNo: original.expenseNo,
      createdAt: original.createdAt, updatedAt: serverTimestamp(), createdBy: original.createdBy,
    }));
    post(original.id, original.expenseNo, row, amt, date, currency);
  });

  const year = new Date().getFullYear();
  plan.creates.forEach(({ row, ref, txRef }, i) => {
    const amt  = rowAmount(row);
    const date = row.date || ctx.saleDate;
    const currency = (row.payNow ? accts.get(row.bankAccountId)?.currency : undefined) ?? ctx.baseCurrency;
    const expenseNo = `${ctx.prefix.toUpperCase()}-${year}-${String(ctx.counterStart + i + 1).padStart(4, '0')}`;
    if (row.payNow && txRef) {
      tx.set(txRef, { ...bankTx(row, amt, date, ref.id), createdAt: new Date().toISOString(), createdBy: ctx.userId });
      move(row.bankAccountId, -amt);
    }
    tx.set(ref, stripUndef({
      ...docFields(row, amt, date, currency, txRef?.id),
      expenseNo,
      createdAt: serverTimestamp(), updatedAt: serverTimestamp(), createdBy: ctx.userId,
    }));
    post(ref.id, expenseNo, row, amt, date, currency);
  });

  // One update per account — a transaction can't modify the same doc twice.
  balanceDelta.forEach((delta, accId) => {
    if (Math.abs(delta) < 0.005) return;
    tx.update(plan.accRefs.get(accId)!, { currentBalance: round2(accts.get(accId)!.currentBalance + delta) });
  });
}

/** Goods revenue of a sale: its invoice total minus re-billed charges and Indian GST. */
export const saleRevenueOf = (inv: { totalAmountUSD?: number; chargesUSD?: number; taxTotalUSD?: number | null }) =>
  (inv.totalAmountUSD ?? 0) - (inv.chargesUSD ?? 0) - (inv.taxTotalUSD ?? 0);
